import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDrilldownParams, EDrilldownMode } from '@op/hooks/useDrilldownParams';
import { getChartClickParams } from '@op/utils/getChartClickParams';
import { Table } from 'tea-component';
import { JumpType } from '@home/utils/utils';
// paramsKey = "channel"
// paramsKey = {"start_time": "",end_time: "", date_type: "",channel: ""}

const { selectable } = Table.addons;
export function useEChartsEvent({
  key,
  paramsKey,
  formatParamsKey,
  defaultProps,
  defaultFilter,
  defaultConfig,
  chartType,
  isDetail,
  updateDetailFilter,
  specialParamsKey,
  jumpType = JumpType.HistoryPush,
}: {
  key: string;
  paramsKey: { [chartType: string]: any } | string;
  formatParamsKey?: (
    eventKeyValue: string | number | any,
    key: string,
  ) => { [key: string]: any } | string;
  defaultProps: { [key: string]: any };
  defaultFilter: { [key: string]: any };
  defaultConfig?: { [key: string]: any };
  chartType?: string;
  isDetail: boolean;
  updateDetailFilter?: (filter: any) => void;
  specialParamsKey?: { [chartType: string]: any } | string;
  jumpType?: JumpType;
}) {
  const [params, setParams] = useState();
  const [extraProps, setExtraProps] = useState<any>();
  const { goPage: goDrilldownPage } = useDrilldownParams({ jumpType });
  const { detailPage } = defaultProps;

  function goPage({ filter, config }: { filter?: any; config?: any }) {
    if (isDetail) {
      updateDetailFilter?.(params);
      return;
    }
    // 下钻到 Detail（文档标注的服务端缓存页）→ Server 模式，URL 带 _s=server 标记
    goDrilldownPage({
      path: '/opinion/Detail',
      filter: {
        key,
        filter: { ...defaultFilter, ...filter },
        title: defaultProps?.title,
        titleCn: defaultProps?.titleCn,
        defaultProps: { ...defaultProps, ...extraProps },
        extraConfig: { ...defaultConfig, ...config },
      },
      mode: EDrilldownMode.Server,
      stats: { reportName: window.location.pathname },
    });
  }

  useEffect(() => {
    if (!params) return;

    goPage({
      config: params,
    });
  }, [params]);

  const getParams = (
    event: any,
    paramsKey: { [chartType: string]: any } | string,
    paramsChartType?: string,
  ) => {
    return getChartClickParams({
      event,
      paramsKey,
      chartType,
      paramsChartType,
      dateType: defaultFilter?.date_type || 'daily',
      formatParamsKey,
    });
  };

  const eventsDict = useMemo(() => {
    if (!isDetail && !detailPage) {
      return undefined;
    }
    if (chartType !== undefined) {
      if (chartType === 'table') {
        const tableEvents: {
          addons: ReturnType<typeof selectable>[];
          onTableCellClick?: (record: any, columnKey?: string) => void;
        } = {
          addons: [
            selectable({
              value: [],
              onChange: (_value: string[], context: any) => {
                const { record } = context || {};
                const params = getParams(record, paramsKey);
                setParams(params);
              },
              rowSelect: true,
            }),
          ],
        };

        if (typeof paramsKey === 'object' && paramsKey.cellClick) {
          tableEvents.onTableCellClick = (record: any, columnKey?: string) => {
            const event = columnKey ? { ...record, columnKey } : record;
            const params = getParams(event, paramsKey, 'cellClick');
            setParams(params);
          };
        }

        return tableEvents;
      }
      return {
        click: (event: any) => {
          const params = getParams(event, paramsKey);
          setParams(params);
        },
        legendselectchanged: (params: { selected: any }) => {
          const defaultSelected = params.selected;
          setExtraProps({
            defaultSelected,
          });
        },
      };
    }

    return (value: any) => {
      const params = getParams(value, paramsKey);
      setParams(params);
    };
  }, [chartType, detailPage]);

  const onChartReady = useCallback(
    (chart: any) => {
      chart.getZr().on('click', (zrEvent) => {
        if (zrEvent?.target) return;

        if (chartType === 'bar') {
          const point = [zrEvent.offsetX, zrEvent.offsetY];
          if (!chart.containPixel('grid', point)) return;

          const [xValue, _yValue] = chart.convertFromPixel(
            { seriesIndex: 0 },
            point,
          );
          const option = chart.getOption();
          if (specialParamsKey) {
            const xAxisData = option.xAxis[0].data;
            const index = Math.round(xValue);

            if (index >= 0 && index < xAxisData?.length) {
              const selected = xAxisData[index];
              const params = getParams({ selected }, specialParamsKey);
              setParams(params);
            }
          }
          return;
        }
        if (chartType === 'sentiment-s-bar') {
          const point = [zrEvent.offsetX, zrEvent.offsetY];
          if (!chart.containPixel('grid', point)) return;

          const [_xValue, yValue] = chart.convertFromPixel(
            { seriesIndex: 0 },
            point,
          );
          const option = chart.getOption();
          if (specialParamsKey) {
            const yAxisData = option.yAxis[0].data;
            const index = Math.round(yValue);

            if (index >= 0 && index < yAxisData?.length) {
              const selected = yAxisData[index];
              // 取值逻辑 & 后续可加新paramsKey处理如果多chartType
              const params = getParams({ selected }, specialParamsKey);
              setParams(params);
            }
          }
        }
        return;
      });
    },
    [setParams],
  );

  return {
    eventsDict,
    onChartReady,
  };
}
